/**
 * Device-class detection, as a pure function.
 *
 * Separate from `device.service.ts` and free of Angular imports so the branches below can be
 * exercised directly. Every one of them exists for a specific device that does not describe
 * itself the way you would expect, and they need to be expressible in a test without a device
 * farm.
 */

/** The parts of `navigator` that {@link detectHandheldDevice} reads. */
export interface DeviceSignals {
  userAgent: string;
  /** `navigator.platform`. Deprecated in the standard, still the only iPadOS tell. */
  platform: string;
  maxTouchPoints: number;
  /** `navigator.userAgentData.mobile`, where the browser implements Client Hints. */
  uaMobile?: boolean;
}

/** Is this a phone or a tablet, as opposed to a laptop or desktop? */
export const detectHandheldDevice = (signals: DeviceSignals): boolean => {
  /*
    Client Hints first, where available: structured data rather than a string to pattern-match,
    and it survives the user agent being reduced further.

    Only trusted when true. The spec's `mobile` means phone-like, so Android tablets and iPads
    report false — and a tablet is a handheld for our purposes. A false here means "not a
    phone", not "not a handheld", so it falls through to the checks below.
  */
  if (signals.uaMobile === true) {
    return true;
  }

  // Phones and Android tablets. `Android` alone covers Android tablets, which omit `Mobile`;
  // that omission is exactly how the two are told apart, and we want both.
  if (
    /Android|iPhone|iPod|iPad|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile/i.test(
      signals.userAgent,
    )
  ) {
    return true;
  }

  /*
    iPadOS 13+ requests desktop sites by default and reports itself as macOS — its user agent
    is indistinguishable from a MacBook's. Multi-touch is the distinguishing signal: no Mac
    reports more than one touch point, and every iPad reports five.

    Note that this is the one place touch is consulted, and only alongside `MacIntel`. Touch on
    its own would catch every touchscreen Windows laptop, which is a computer whose way around
    the app is the navigation menu.

    If `platform` is ever removed, an iPad falls back to being treated as a computer — the
    navigation stays visible rather than the screen breaking.
  */
  if (signals.platform === 'MacIntel' && signals.maxTouchPoints > 1) {
    return true;
  }

  return false;
};
